import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSalesBlink } from "@/hooks/useSalesBlink";
import type { NewsletterLead } from "@/components/workflow/leads/types";

interface LeadEnrichmentProps {
  lead: NewsletterLead;
  onEnrichmentComplete: () => void;
}

export const LeadEnrichment = ({ lead, onEnrichmentComplete }: LeadEnrichmentProps) => {
  const { toast } = useToast();
  const { enrichLead } = useSalesBlink();
  const [isEnriching, setIsEnriching] = useState(false);

  const handleEnrich = async () => {
    try {
      setIsEnriching(true);
      await enrichLead({
        email: lead.email,
        company: lead.company,
        metadata: lead.metadata,
      });

      toast({
        title: "Lead enriched",
        description: "Lead data has been successfully enriched.",
      });
      
      onEnrichmentComplete();
    } catch (error) {
      console.error("Error enriching lead:", error);
      toast({
        title: "Error",
        description: "Failed to enrich lead data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsEnriching(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lead Enrichment</CardTitle>
      </CardHeader>
      <CardContent>
        <Button 
          onClick={handleEnrich} 
          disabled={isEnriching}
          className="w-full"
        >
          {isEnriching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEnriching ? "Enriching..." : "Enrich Lead Data"}
        </Button>
      </CardContent>
    </Card>
  );
};